import type {
  AiStoryGenerationResult,
  AiStoryScenePayload,
  SceneListItem,
  TextLine,
} from "@chuzi/shared/types";
import { resolveSceneTextFont } from "@chuzi/shared/config";
import { api } from "./api";
import { SCROLLABLE_TEXT_FILL } from "../components/generator-timeline";

/** Bottom-right arrow facing right (chevron rotated 90°). */
export const CHUZI_AI_CHOICE_DEFAULTS = {
  x: 82,
  y: 78,
  w: 12,
  h: 14,
  arrow_rotation: 90,
  arrow_scale: 1,
} as const;

/** Default scene playback length for Chuzi AI scenes (seconds). */
export const CHUZI_AI_SCENE_DURATION_SEC = 5;

/** When the continue choice fades in (seconds). */
export const CHUZI_AI_CONTINUE_APPEAR_SEC = 3;

function continueChoiceProperties(targetSceneId: string): Record<string, unknown> {
  return {
    label: "viewer_continue",
    target_scene_id: targetSceneId,
    ...CHUZI_AI_CHOICE_DEFAULTS,
    fade_in_ms: 600,
    fade_out_ms: 0,
    duration_ms: 2000,
    start_time_seconds: CHUZI_AI_CONTINUE_APPEAR_SEC,
    end_time_seconds: CHUZI_AI_SCENE_DURATION_SEC + 1,
    pause_for_choice: false,
  };
}

function genLineId(): string {
  return crypto.randomUUID();
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Normalize AI/plain text before HTML composition. */
function normalizePlainText(text: string): string {
  return text.replace(/\\n/g, "\n").replace(/\r\n/g, "\n");
}

function textToHtml(text: string): string {
  const normalized = normalizePlainText(text);
  const paragraphs = normalized
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const paragraphStyle = "margin:0;line-height:1.12;text-align:left;";
  const body =
    paragraphs.length > 0
      ? paragraphs
          .map((p) => escapeHtml(p).replace(/\n/g, "<br>"))
          .join("<br><br>")
      : escapeHtml(normalized.trim()).replace(/\n/g, "<br>");
  if (!body) return "<p></p>";
  return `<p style="${paragraphStyle}">${body}</p>`;
}

function buildScrollableTextLine(html: string): TextLine {
  return {
    id: genLineId(),
    type: "text",
    html,
    appear_at_ms: 0,
    fade_in_ms: 400,
    fade_out_ms: 0,
    duration_ms: 120_000,
    persist: true,
    ...SCROLLABLE_TEXT_FILL,
  };
}

export function scenePayloadToUpdate(scene: AiStoryScenePayload) {
  const html = textToHtml(scene.text);
  const lines = [buildScrollableTextLine(html)];
  const font = resolveSceneTextFont(scene.font_family);
  return {
    media_mode: "text" as const,
    text_content: { html, lines },
    text_style: {
      font_family: font.id,
      text_color: scene.text_color,
      background_color: scene.background_color,
      font_size: scene.font_size ?? 100,
    },
    color: scene.scene_color,
    choice_end_time_seconds: CHUZI_AI_SCENE_DURATION_SEC,
  };
}

/**
 * Create story + content scenes from a Chuzi AI generation result.
 * When `existingStoryId` is provided, scenes are added to that story
 * instead of creating a new one. Creates content scenes from the generation,
 * removes auto-created bookend scenes so only real content remains.
 */
export async function applyChuziAiStory(
  generation: AiStoryGenerationResult,
  existingStoryId?: string,
): Promise<{ storyId: string; storyTitle: string; contentSceneIds: string[] }> {
  let storyId: string;
  if (existingStoryId) {
    storyId = existingStoryId;
  } else {
    const storyRes = await api.stories.create({ title: generation.title });
    storyId = storyRes.data.id;
  }

  const existing = await api.scenes.index(storyId);
  const titleScene = existing.find((s) => s.is_title);
  const endScene = existing.find((s) => s.is_end);

  const contentSceneIds: string[] = [];
  let order = 1;

  for (const payload of generation.scenes) {
    const created = await api.scenes.create({
      story_id: storyId,
      title: payload.title,
      order,
    });
    order += 1;
    contentSceneIds.push(created.id);
    await api.scenes.update(created.id, scenePayloadToUpdate(payload));
  }

  if (titleScene) await api.scenes.destroy(titleScene.id);
  if (endScene) await api.scenes.destroy(endScene.id);

  // Wire "continue" choices between consecutive content scenes
  for (let i = 0; i < contentSceneIds.length - 1; i++) {
    await api.sceneActions.create({
      scene_id: contentSceneIds[i],
      name: "Next",
      type: "choice",
      order: 0,
      duration: 0,
      properties: continueChoiceProperties(contentSceneIds[i + 1]),
    });
  }

  return { storyId, storyTitle: generation.title, contentSceneIds };
}

function sortScenesForApply(scenes: SceneListItem[]): SceneListItem[] {
  return [...scenes].sort((a, b) => {
    if (a.is_title !== b.is_title) return a.is_title ? -1 : 1;
    return a.order - b.order || a.created_at.localeCompare(b.created_at);
  });
}

/** Credits needed to materialize scenes after AI generation (story + N content scenes). */
export function sceneBuildCredits(sceneCount: number): number {
  return 1 + Math.max(0, sceneCount);
}

export async function applyChuziAiSceneOverwrite(
  sceneId: string,
  payload: AiStoryScenePayload,
): Promise<SceneListItem> {
  return api.scenes.update(sceneId, {
    title: payload.title,
    ...scenePayloadToUpdate(payload),
  });
}

/**
 * Create a new content scene from Chuzi AI output and wire a default next-scene choice.
 */
export async function applyChuziAiSceneCreate(
  storyId: string,
  payload: AiStoryScenePayload,
  scenes: SceneListItem[],
  insertAfterSceneId?: string | null,
): Promise<SceneListItem> {
  const sorted = sortScenesForApply(scenes);
  const contentScenes = sorted.filter((s) => !s.is_title);

  let order: number;
  let successorId: string | undefined;

  if (insertAfterSceneId) {
    const anchor = sorted.find((s) => s.id === insertAfterSceneId);
    const anchorIdx = contentScenes.findIndex((s) => s.id === insertAfterSceneId);
    if (anchor && anchorIdx >= 0) {
      order = anchor.order + 1;
      successorId = contentScenes[anchorIdx + 1]?.id;
    } else {
      order = (contentScenes[contentScenes.length - 1]?.order ?? 0) + 1;
    }
  } else {
    order = (contentScenes[contentScenes.length - 1]?.order ?? 0) + 1;
  }

  const created = await api.scenes.create({
    story_id: storyId,
    title: payload.title,
    order,
  });

  const updated = await api.scenes.update(created.id, scenePayloadToUpdate(payload));

  if (successorId) {
    await api.sceneActions.create({
      scene_id: updated.id,
      name: "Next",
      type: "choice",
      order: 0,
      duration: 0,
      properties: continueChoiceProperties(successorId),
    });
  }

  return updated;
}
